import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { InputBuscarComponent } from '../../../shared/components/input-buscar/input-buscar.component';
import { PaginacaoComponent } from '../../../shared/components/paginacao/paginacao.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { ScrollNavegacaoService } from '../../../core/services/scroll-navegacao.service';
import { CANAIS, CanalResultado, ResultadoBusca } from '../models/busca.model';
import { BuscaPublicaService, TERMO_MINIMO, termoValido } from '../services/busca-publica.service';
import { ordenarResultados } from './ordenar-resultados';
import { casarPaginas } from './paginas-portal.catalogo';

const RESULTADOS_POR_PAGINA = 4;

/**
 * A tela `/resultado-busca?q=`: os serviços, categorias e notícias que a API encontrou para
 * o termo, mais as páginas fixas do portal que casam com ele, numa lista só, por relevância.
 *
 * O termo vem do `q` da URL (`bindToComponentInputs`). Buscar de novo pela barra da própria
 * tela navega com o `q` novo, e o `input()` atualiza o termo — não há segundo caminho.
 */
@Component({
  selector: 'app-resultado-busca',
  templateUrl: './resultado-busca.component.html',
  styleUrls: ['./resultado-busca.component.css'],
  imports: [RouterLink, LucideModule, BreadcrumbComponent, InputBuscarComponent, PaginacaoComponent],
})
export class ResultadoBuscaComponent {
  private readonly injector = inject(Injector);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);
  private readonly buscaPublica = inject(BuscaPublicaService);

  private readonly lista = viewChild<ElementRef<HTMLElement>>('listaResultados');
  readonly q = input('');

  readonly canais = CANAIS;
  readonly termoMinimo = TERMO_MINIMO;

  readonly termo = linkedSignal(() => this.q());
  readonly canalAtivo = signal<CanalResultado | ''>('');

  /** O recurso da API, ocioso enquanto o termo não tiver o mínimo. */
  private readonly busca = this.buscaPublica.buscar(() => this.termo());

  readonly termoCurto = computed(() => !termoValido(this.termo()));
  readonly carregando = this.busca.carregando;
  readonly falhou = this.busca.falhou;

  /** Registros da API primeiro, páginas depois; a ordem final é a da relevância. */
  readonly resultados = computed<readonly ResultadoBusca[]>(() =>
    this.termoCurto()
      ? []
      : ordenarResultados(this.termo(), [...this.busca.itens(), ...casarPaginas(this.termo())])
  );

  readonly resultadosDoCanal = computed<readonly ResultadoBusca[]>(() => {
    const canal = this.canalAtivo();
    return canal ? this.resultados().filter((r) => r.canal === canal) : this.resultados();
  });

  readonly total = computed(() => this.resultados().length);

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.resultadosDoCanal().length / RESULTADOS_POR_PAGINA))
  );

  readonly paginaAtual = linkedSignal({
    source: () => ({ canal: this.canalAtivo(), termo: this.termo() }),
    computation: () => 1,
  });

  readonly resultadosDaPagina = computed<readonly ResultadoBusca[]>(() => {
    const inicio = (this.paginaAtual() - 1) * RESULTADOS_POR_PAGINA;
    return this.resultadosDoCanal().slice(inicio, inicio + RESULTADOS_POR_PAGINA);
  });

  totalDoCanal(canal: CanalResultado): number {
    return this.resultados().filter((resultado) => resultado.canal === canal).length;
  }

  rotuloCanal(canal: CanalResultado): string {
    return this.canais.find((opcao) => opcao.id === canal)?.rotulo ?? '';
  }

  selecionarCanal(canal: CanalResultado | ''): void {
    this.canalAtivo.set(canal);
  }

  aoBuscar(termo: string): void {
    this.termo.set(termo);
    this.canalAtivo.set('');
  }

  tentarNovamente(): void {
    this.busca.recarregar();
  }

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);

    afterNextRender(() => this.rolarParaLista(), { injector: this.injector });
  }

  private rolarParaLista(): void {
    this.scrollNavegacao.aoElemento(this.lista()?.nativeElement);
  }
}
